import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { Loading } from '@/components/Skeleton';
import { cn } from '@/lib/utils';
import { DASHBOARD_COPY } from './copy';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE TILE SHELL. [P6a]
 *
 *  ⚠️ THE WHOLE CARD IS THE DRILL TARGET, AND IT IS DONE WITH A STRETCHED PSEUDO-ELEMENT
 *  RATHER THAN AN `<a>` AROUND THE CARD. An anchor may not contain interactive descendants,
 *  and a tile holds a Retry button and — for two of them — a body of row links.
 *  `after:absolute after:inset-0` on the TITLE link gives the card a hit area while the DOM
 *  holds exactly ONE link with a short accessible name: ⌘-click works, copy-link works, the
 *  destination shows in the status bar, and a screen reader does not read the whole tile
 *  aloud as one blob. `relative` on the article is what gives the overlay a containing block.
 *
 *  ⚠️ THE COST, STATED RATHER THAN DISCOVERED: an overlay makes the tile's text unselectable.
 *  Fine for a numeral; wrong for a list somebody copies out — which is exactly why
 *  `drillReach: 'footer'` exists and is declared in the registry beside the renderer, where a
 *  call site cannot get it wrong.
 *
 *  ⚠️ THE HINT IS A `<p>`, NEVER A `Tooltip`. Three independent reasons: divergence #82
 *  measured an AntD tooltip per list row taking one test file from 64s to 119s; hover is
 *  unreachable from a keyboard and unavailable on touch ([D4.3]); and it would create an
 *  overlay site `census.spec.ts` requires to be accounted for by name. A dashboard's whole
 *  job is being scannable, and a sentence you must hover for is not.
 *
 *  ─── ⚠️ [amended C2b §6a.1] THE FOOTER WAS NOT CLICKABLE, IN EITHER MODE ────────────────
 *
 *  Reported, and true twice over. The footer rendered `{drill.label} →` as an `aria-hidden`
 *  `<p>` carrying `relative z-10`:
 *
 *    · under `drillReach: 'card'`, that `z-10` stacked it ABOVE the stretched
 *      `after:absolute after:inset-0` overlay, so a click landing exactly on the words hit the
 *      paragraph and did nothing — the one part of the card that looks like a control was the
 *      one part that was not one;
 *    · under `drillReach: 'footer'` there is no overlay at all and the `<p>` was never a link,
 *      so the ONLY drill target was the title text.
 *
 *  ⚠️ AND ELEVEN OF NINETEEN TILES ARE `'footer'`. On most of the dashboard the thing that
 *  looked like a control was inert and the thing that worked looked like a heading.
 *
 *  The fix keeps ONE anchor per card, which is what §4 is actually about:
 *    · `'card'`   — the footer is `pointer-events-none`, so the overlay takes the click. The
 *                   label works because the CARD does.
 *    · `'footer'` — the footer IS the anchor and the title is plain text. Its accessible name
 *                   carries the TILE'S OWN TITLE, or eleven tiles announce "Open the inbox".
 *
 *  ⚠️ ONLY A BROWSER CAN SEE THE HIT TEST. jsdom resolves no cascade and reads neither
 *  `z-index` nor `pointer-events` — `dashboard.spec.ts` already uses `document.elementFromPoint`
 *  for exactly this class of claim.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

export type DrillReach = 'card' | 'footer';

export type TileCardProps = {
  id: string;
  /**
   * ⚠️ [P6b.2] THE PLACEMENT'S IDENTITY, AND ITS ABSENCE IS A REAL DEFECT RATHER THAN A
   * MISSING TEST HANDLE.
   *
   * `SHORTCUT` is `repeatable`, so one dashboard can hold several of it — and every
   * `id`-derived string here is then DUPLICATED across two elements. `headingId` is the one
   * that matters: two nodes with `id="tile-SHORTCUT-heading"` make `aria-labelledby`
   * ambiguous, and a screen reader resolves it to the FIRST, so the second door announces the
   * first door's name. `data-testid` collapsing is the same defect wearing a test hat — a
   * strict `getByTestId` then fails with a duplicate rather than with whatever went wrong.
   *
   * It is OPTIONAL because a caller with exactly one of a tile (a drill-through page) has no
   * placement to name, and defaulting to `id` keeps every existing selector working.
   */
  instanceId?: string;
  title: string;
  /**
   * ⚠️ [amended C1e §3] `null` IS A REAL VALUE NOW AND MEANS "RENDER NO LINE".
   * The render below is already `{hint ? <p/> : null}`, which is falsy for `undefined`, `null`
   * AND `''` — so the element's absence falls out of the existing expression and this is a
   * type widening rather than a branch. `resolveTileWords` is where the tri-state is decided.
   */
  hint?: string | null;
  spanClassName: string;
  /**
   * ⚠️ [C2b §6b] WHICH ACCENT THIS CARD'S EDGE CARRIES.
   *
   * It is the tile's EXISTING `DASHBOARD_TILE_CATEGORIES` value, passed in rather than derived
   * from `id` — `id` is typed `string` here so the card stays presentational, and a lookup
   * would need a cast in the one component that should not know the registry exists.
   *
   * ⚠️ `NAVIGATION` DELIBERATELY TAKES THE ANALYTICAL ACCENT rather than a third colour. A
   * shortcut is a door, not a figure; a third hue on the grid would be the per-group palette
   * [P6b §3d] refused, for a category with one member.
   */
  category?: 'OPERATIONAL' | 'ANALYTICAL' | 'NAVIGATION';
  /**
   * ⚠️ [P6b] `'preview'` IS A FOURTH STATE, AND IT EXISTS TO REMOVE TWO THINGS.
   *
   * The configurator draws the real card at its real width with its real silhouette, and
   * `'loading'` would have been the obvious reuse. It is wrong twice: `<Loading>` is a
   * `role="status"` LIVE REGION, so a fourteen-tile preview announces "Loading…" fourteen
   * times on a page where nothing is loading; and the title would still render as a `<Link>`,
   * so a ⌘-click would carry the operator out of an unsaved draft into somebody else's inbox.
   *
   * With this branch there is NO VALUE of the preview's props that produces an anchor —
   * a structural guarantee rather than a discipline at the call site.
   */
  status: 'ready' | 'loading' | 'error' | 'preview';
  /** `undefined` renders the title as PLAIN TEXT — a real state, not a missing prop. */
  drill?: { to: string; label: string };
  drillReach?: DrillReach;
  skeleton: ReactNode;
  error?: string;
  retryable?: boolean;
  onRetry?: () => void;
  retrying?: boolean;
  children: ReactNode;
};

export const TileCard = ({
  id,
  instanceId,
  title,
  hint,
  spanClassName,
  category,
  status,
  drill,
  drillReach = 'card',
  skeleton,
  error,
  retryable,
  onRetry,
  retrying,
  children,
}: TileCardProps) => {
  const key = instanceId ?? id;
  const headingId = `tile-${key}-heading`;
  /* ⚠️ A PREVIEW NAVIGATES NOWHERE. The drill LABEL still renders, as the plain text it
     already was, because the operator may have overridden it and needs to see it. */
  const linked = drill && status !== 'preview' ? drill : undefined;
  /*
   * ⚠️ [C2b §6a.1] WHICH ELEMENT IS THE ANCHOR — exactly one of them, never both.
   * `'footer'` moves it down because that card has no overlay and its rows hold controls;
   * `'card'` keeps it on the title, where the stretched overlay hangs off it.
   */
  const footerIsLink = linked !== undefined && drillReach === 'footer';

  return (
    <article
      /*
       * ⚠️ [P6b.2] THE TEST ID AND `data-tile` STAY KEYED ON THE TILE; ONLY `headingId` AND
       * `data-instance` KEY ON THE PLACEMENT — AND THE ASYMMETRY IS DELIBERATE.
       *
       * Almost every caller here means "the SWAPS tile", which is a tile-level question and
       * has one answer; the configurator's `layout-entry-*` went the other way because every
       * row in THAT list is a placement and a caller there always means one row. The DOM `id`
       * has no such choice — a duplicate makes `aria-labelledby` resolve to the first node,
       * so the second shortcut would announce the first one's name.
       *
       * ⚠️ A caller that genuinely means ONE placement of a repeatable tile reads
       * `data-instance`; `getByTestId('tile-SHORTCUT')` is ambiguous on a dashboard with two
       * doors, and that is what `getAllByTestId` is for.
       */
      data-testid={`tile-${id}`}
      data-tile={id}
      data-instance={key}
      data-state={status}
      aria-labelledby={headingId}
      className={cn(
        'relative flex min-w-0 flex-col gap-3 rounded-lg border border-border bg-card p-4',
        'focus-within:ring-2 focus-within:ring-ring',
        /*
         * ⚠️ [C2b §6b] A LEFT RULE, AND IT IS THE ONLY COLOUR THIS SHELL ADDS.
         *
         * `border-l-2` widens ONE edge — the card keeps its hairline on the other three, so
         * nothing about its footprint changes and the grid does not reflow. In `standard` both
         * tokens alias `border`, so the rule is the same colour as the border it replaces and
         * the render is byte-identical; only `colourful` colours it.
         *
         * ⚠️ NOTHING LANDS NEXT TO A NUMBER. The rule is on the card's outer edge, where
         * red/amber/green (fulfilment) and blue (interactive) never appear.
         */
        category === undefined
          ? null
          : category === 'OPERATIONAL'
            ? 'border-l-2 border-l-tile-accent-operational'
            : 'border-l-2 border-l-tile-accent-analytical',
        spanClassName,
      )}
    >
      <h3 id={headingId} className="text-sm font-medium text-foreground">
        {linked && !footerIsLink ? (
          <Link
            to={linked.to}
            className="rounded-sm text-link underline-offset-4 hover:underline after:absolute after:inset-0"
          >
            {title}
          </Link>
        ) : (
          title
        )}
      </h3>
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}

      {status === 'preview' ? (
        /* No live region: nothing is loading, and fourteen of them would say so anyway. */
        <div aria-hidden>{skeleton}</div>
      ) : status === 'loading' ? (
        <Loading label={DASHBOARD_COPY.loadingTile(title)}>{skeleton}</Loading>
      ) : status === 'error' ? (
        <div className="relative z-10 flex flex-col items-start gap-2">
          <p className="text-sm text-muted-foreground">{error}</p>
          {/* shadcn's Button carries no `loading` prop — `disabled` is the whole of it. */}
          {retryable && onRetry ? (
            <Button variant="outline" size="sm" onClick={onRetry} disabled={retrying}>
              {DASHBOARD_COPY.tileRetry}
            </Button>
          ) : null}
        </div>
      ) : (
        children
      )}

      {drill ? (
        footerIsLink && linked ? (
          <Link
            to={linked.to}
            /* ⚠️ THE TILE'S OWN TITLE IS IN THE NAME, or eleven cards announce the same words. */
            aria-label={`${drill.label} — ${title}`}
            /*
             * ⚠️ `drill-footer`, NOT `tile-footer`. `Dashboard.test.tsx` sweeps
             * `getAllByTestId(/^tile-/)` to read the tile ORDER off the page, so anything else
             * in that namespace joins the list as an `undefined` id — five tests went red on a
             * name, which is the cheapest possible reminder that a test id is a NAMESPACE.
             */
            data-testid="drill-footer"
            className="relative z-10 mt-auto w-fit rounded-sm text-xs text-link underline-offset-4 hover:underline"
          >
            {drill.label} →
          </Link>
        ) : (
          /*
           * ⚠️ `pointer-events-none` IS THE WHOLE FIX FOR THE `'card'` MODE. `z-10` puts this
           * above the title's stretched overlay — it has to, or the Retry button beside it is
           * unreachable — so without this the label swallowed the click the card would have
           * taken. It is decoration here; the card is the control.
           */
          <p
            aria-hidden
            data-testid="drill-footer"
            className="pointer-events-none relative z-10 mt-auto text-xs text-muted-foreground"
          >
            {drill.label} →
          </p>
        )
      ) : null}
    </article>
  );
};
